import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import BrandAvatar from './BrandAvatar';
import NotificationBell from './NotificationBell';
import InstallPrompt from './InstallPrompt';
import { LayoutDashboard, Database, Users, Megaphone, MessageSquare, BarChart3, Settings, LogOut, Menu, X, Bot, Inbox, Plug } from 'lucide-react';
import { hasFeature } from '../utils/features';
import { useLateral, ItemNav, BotonColapsar, fondoDeMarca, SOBRE_DEGRADE } from './lateral';
import api from '../utils/api';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true, feature: 'dashboard' },
  { to: '/datasets', icon: Database, label: 'Mis Bases', feature: 'datasets' },
  { to: '/leads', icon: Users, label: 'Todos los Leads', feature: 'leads' },
  { to: '/inbox', icon: Inbox, label: 'Conversaciones', feature: 'inbox' },
  { to: '/campaigns', icon: Megaphone, label: 'Campañas', feature: 'campaigns' },
  { to: '/templates', icon: MessageSquare, label: 'Plantillas', feature: 'templates' },
  { to: '/reports', icon: BarChart3, label: 'Reportes', feature: 'reports' },
  { to: '/bot', icon: Bot, label: 'Agente IA', feature: 'bot' },
  { to: '/channels', icon: Plug, label: 'Canales', feature: 'channels' },
  { to: '/settings', icon: Settings, label: 'Configuración', feature: 'settings' },
];

export default function Layout() {
  const { logout, user, tenant, theme, setTheme } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const lateral = useLateral();

  useEffect(() => {
    if (tenant?.company_name) { setClientName(tenant.company_name); }
    else { api.get('/settings').then(res => setClientName(res.data.client_name || '')).catch(() => {}); }
  }, [tenant]);

  const brandName = tenant?.company_name || tenant?.name || clientName || 'Mi marca';
  const fondo = fondoDeMarca(tenant?.primary_color);
  const BORDE = '1px solid rgba(255,255,255,0.12)';

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden' }}>
      <button onClick={() => setMobileOpen(!mobileOpen)} className="mobile-menu-btn"
        style={{ position:'fixed', top:12, left:12, zIndex:1001, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, padding:8, color:'var(--text-primary)', display:'none' }}>
        {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
      </button>
      {mobileOpen && <div style={{position:'fixed',inset:0,background:'var(--overlay)',zIndex:998}} onClick={()=>setMobileOpen(false)}/>}
      <div {...lateral.props}>
      <aside className={'sidebar' + (lateral.abierto ? ' abierto' : '')}
        style={{ width: lateral.ancho, height:'100vh', ...fondo, display:'flex', flexDirection:'column',
          position: mobileOpen ? 'fixed' : 'absolute', ...SOBRE_DEGRADE }}>
        <div style={{ padding: lateral.abierto ? '24px 20px' : '22px 0', borderBottom:BORDE }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent: lateral.abierto ? 'flex-start' : 'center' }}>
            <BrandAvatar src={tenant?.logo_url} name={brandName} size={lateral.abierto ? 48 : 38} />
            {lateral.abierto && (
            <div className="sidebar-brand-text" style={{ minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:800, color:'#fff', lineHeight:1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={brandName}>{brandName}</div>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4, fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:600 }}>
                <img src="/favicon-32x32.png?v=2" alt="" style={{ width:20, height:20, borderRadius:5, border:'1px solid rgba(255,255,255,0.7)' }} /> Udream App
              </div>
            </div>
            )}
          </div>
        </div>
        {hasFeature(tenant, user, 'notifications') && (
          <div style={{ padding:'10px 10px 0' }}><NotificationBell abierto={lateral.abierto} /></div>
        )}
        <nav style={{ flex:1, padding:'8px 10px 12px', display:'flex', flexDirection:'column', gap:2, overflowY:'auto' }}>
          {navItems.filter(item => hasFeature(tenant, user, item.feature)).map(item => (
            <ItemNav key={item.to} to={item.to} end={item.end} icono={item.icon} label={item.label}
              abierto={lateral.abierto} onClick={() => setMobileOpen(false)} />
          ))}
        </nav>
        <div style={{ padding: lateral.abierto ? '12px 14px 16px' : '12px 10px 16px', borderTop:BORDE }}>
          <BotonColapsar colapsado={lateral.colapsado} onClick={lateral.alternar} abierto={lateral.abierto} />
          {lateral.abierto && (
            <div style={{ display:'flex', alignItems:'center', gap:8, margin:'10px 0 8px' }}>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', minWidth:0, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.display_name || user?.username}</div>
              <ThemeToggle theme={theme} onChange={setTheme} color="rgba(255,255,255,0.8)" />
            </div>
          )}
          <button onClick={logout} className={'nav-item accion' + (lateral.abierto ? '' : ' solo-icono')} title="Cerrar sesión">
            <LogOut size={19}/>{lateral.abierto && <span className="nav-texto">Cerrar sesión</span>}
          </button>
        </div>
      </aside>
      </div>
      <main style={{ flex:1, overflow:'auto', padding:28 }}><Outlet/></main>

      <InstallPrompt />
      <style>{`
        @media (max-width: 768px) {
          .sidebar { position: fixed !important; transform: translateX(${mobileOpen ? '0' : '-100%'}) !important; transition: transform 0.25s; }
          .mobile-menu-btn { display: block !important; }
          main { padding: 16px !important; padding-top: 56px !important; }
          .sidebar > div:first-child { padding-top: 56px !important; }
        }
      `}</style>
    </div>
  );
}
